import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Grid, Paper, Typography, Table, TableHead, TableRow, TableCell, TableBody,
  Chip, Button, Stack, LinearProgress, Select, MenuItem, IconButton, Tooltip, Alert, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField,
} from '@mui/material';
import BlockRoundedIcon from '@mui/icons-material/BlockRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import BugReportRoundedIcon from '@mui/icons-material/BugReportRounded';
import PageHeader from '../../components/common/PageHeader';
import ModuleControlPanel from '../../components/common/ModuleControlPanel';
import StatCard from '../../components/common/StatCard';
import { loadEndpoints, isolateDevice, setFindingStatus, approvePendingCommand, rejectPendingCommand, recomputeRisk, registerDevice, simulateRansomware } from './endpointSlice';
import { severityChipSx, severityLabel, formatRelativeTime } from '../../utils/severity';
import { tokens } from '../../theme/theme';

const FINDING_STATUSES = ['OPEN', 'ACKNOWLEDGED', 'RESOLVED', 'FALSE_POSITIVE'];

function riskColor(score) {
  if (score >= 70) return tokens.severity.critical;
  if (score >= 40) return tokens.severity.medium;
  return tokens.severity.low;
}

const AV_COLOR = {
  protected: tokens.severity.low,
  outdated: tokens.severity.medium,
  disabled: tokens.severity.critical
};

export default function EndpointPage() {
  const dispatch = useDispatch();
  const { devices, alerts, commands, error: loadError } = useSelector((s) => s.endpoint);
  const [error, setError] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [registerOpen, setRegisterOpen] = useState(false);
  const [registerForm, setRegisterForm] = useState({ hostname: '', os: '', ip_address: '' });
  const [notice, setNotice] = useState(null);

  useEffect(() => { dispatch(loadEndpoints()); }, [dispatch]);

  const atRisk = devices.filter((d) => d.riskScore >= 70).length;
  const unencrypted = devices.filter((d) => !d.encrypted).length;

  const runAction = async (thunk, arg) => {
    setError(null);
    const result = await dispatch(thunk(arg));
    if (thunk.rejected.match(result)) setError(result.error?.message || 'That action could not be completed.');
  };

  const handleRegister = async () => {
    if (!registerForm.hostname.trim()) return setError('Hostname is required.');
    setError(null);
    const result = await dispatch(registerDevice({
      hostname: registerForm.hostname.trim(),
      os: registerForm.os.trim() || null,
      ip_address: registerForm.ip_address.trim() || null,
      agent_version: 'manual-1.0',
    }));
    if (registerDevice.rejected.match(result)) return setError(result.error?.message || 'Could not register this device.');
    setNotice(`${registerForm.hostname.trim()} registered.`);
    setRegisterOpen(false);
    setRegisterForm({ hostname: '', os: '', ip_address: '' });
  };

  const handleSimulate = async (device) => {
    setError(null);
    const result = await dispatch(simulateRansomware(device.id));
    if (simulateRansomware.rejected.match(result)) return setError(result.error?.message || 'Could not simulate events for this device.');
    setNotice(`Simulated ransomware activity on ${device.hostname}: ${result.payload.detections} detection(s) raised.`);
  };

  const hostnameById = Object.fromEntries(devices.map((d) => [d.id, d.hostname]));

  const handleReject = async () => {
    if (rejectReason.trim().length < 10) return setError('Rejection reason must be at least 10 characters.');
    await runAction(rejectPendingCommand, { id: rejectTarget.id, reason: rejectReason.trim() });
    setRejectTarget(null);
    setRejectReason('');
  };

  return (
    <Box>
      <PageHeader
        title="Endpoint Protection"
        subtitle="Device monitoring, antivirus status, USB control, ransomware and file integrity detection"
        action={<ModuleControlPanel basePath="/endpoints" moduleLabel="Endpoint Protection" />}
      />

      {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>{notice}</Alert>}
      {(error || loadError) && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error || loadError}</Alert>}

      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
        <Grid item xs={12} sm={4}>
          <StatCard label="Devices Monitored" value={devices.length} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard label="High Risk Devices" value={atRisk} accent={tokens.severity.critical} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard label="Unencrypted Devices" value={unencrypted} icon={LockRoundedIcon} accent={unencrypted > 0 ? tokens.severity.medium : tokens.severity.low} />
        </Grid>
      </Grid>

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={7}>
          <Paper sx={{ overflow: 'hidden' }}>
            <Box sx={{ p: 2, pb: 0 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="overline" sx={{ color: tokens.text.muted, fontSize: '0.65rem' }}>Endpoints — by risk</Typography>
                <Button size="small" startIcon={<AddRoundedIcon />} onClick={() => { setError(null); setRegisterOpen(true); }}>Register Device</Button>
              </Box>
            </Box>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Host', 'OS', 'User', 'AV Status', 'Risk', 'Last Seen', ''].map((h) => (
                    <TableCell key={h} sx={{ color: tokens.text.muted, fontSize: '0.68rem', textTransform: 'uppercase' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {devices.map((d) => (
                  <TableRow key={d.id} hover>
                    <TableCell sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.78rem', color: tokens.text.primary }}>
                      {d.hostname}{d.isolated && <Chip label="isolated" size="small" sx={{ ml: 1, height: 16, fontSize: '0.58rem', color: tokens.severity.info, backgroundColor: 'transparent', border: `1px solid ${tokens.severity.info}` }} />}
                      {d.isolationPending && <Chip label="isolation pending approval" size="small" sx={{ ml: 1, height: 16, fontSize: '0.58rem', color: tokens.severity.warning, backgroundColor: 'transparent', border: `1px solid ${tokens.severity.warning}` }} />}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.76rem', color: tokens.text.secondary }}>{d.os}</TableCell>
                    <TableCell sx={{ fontSize: '0.76rem', color: tokens.text.secondary }}>{d.user}</TableCell>
                    <TableCell>
                      <Chip label={d.avStatus} size="small" sx={{ color: AV_COLOR[d.avStatus], border: `1px solid ${AV_COLOR[d.avStatus]}`, backgroundColor: 'transparent' }} />
                    </TableCell>
                    <TableCell sx={{ width: 90 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <LinearProgress
                          variant="determinate" value={d.riskScore}
                          sx={{ width: 44, height: 5, borderRadius: 4, backgroundColor: tokens.bg.elevated, '& .MuiLinearProgress-bar': { backgroundColor: riskColor(d.riskScore), borderRadius: 4 } }}
                        />
                        <Typography sx={{ fontSize: '0.7rem', color: riskColor(d.riskScore), fontFamily: '"JetBrains Mono", monospace' }}>{d.riskScore}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.72rem', color: tokens.text.muted }}>{formatRelativeTime(d.lastSeen)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {!d.isolated && !d.isolationPending && (
                        <Button
                          size="small" color="error" variant="outlined" startIcon={<BlockRoundedIcon fontSize="small" />}
                          onClick={() => dispatch(isolateDevice(d.id))}
                          sx={{ fontSize: '0.68rem', py: 0.2, mr: 0.5 }}
                        >
                          Isolate
                        </Button>
                      )}
                      <Tooltip title="Simulate ransomware activity">
                        <IconButton size="small" onClick={() => handleSimulate(d)}><BugReportRoundedIcon fontSize="small" /></IconButton>
                      </Tooltip>
                      <Tooltip title="Recompute risk score">
                        <IconButton size="small" onClick={() => runAction(recomputeRisk, d.id)}><RefreshRoundedIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {!devices.length && (
                  <TableRow><TableCell colSpan={7} sx={{ textAlign: 'center', color: tokens.text.muted, fontSize: '0.78rem', py: 3 }}>
                    No devices monitored yet — install the endpoint agent or use Register Device, then simulate activity to test detection.
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
        </Grid>

        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 2.4, mb: 2.5 }}>
            <Typography variant="overline" sx={{ color: tokens.text.muted, fontSize: '0.65rem' }}>Endpoint Alerts</Typography>
            <Stack spacing={1.2} sx={{ mt: 1.5 }}>
              {alerts.map((a) => (
                <Box key={a.id} sx={{ p: 1.3, borderRadius: 1.5, border: `1px solid ${tokens.border}`, backgroundColor: tokens.bg.elevated }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, alignItems: 'flex-start' }}>
                    <Typography sx={{ fontSize: '0.8rem', color: tokens.text.primary, fontWeight: 500 }}>{a.type}</Typography>
                    <Chip label={severityLabel(a.severity)} size="small" sx={severityChipSx(a.severity)} />
                  </Box>
                  <Typography sx={{ fontSize: '0.76rem', color: tokens.text.secondary }}>{a.detail}</Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                    <Typography variant="caption" sx={{ color: tokens.text.muted, fontFamily: '"JetBrains Mono", monospace' }}>
                      {hostnameById[a.endpoint_id] || a.hostname} · {formatRelativeTime(a.timestamp)}
                    </Typography>
                    <Select
                      size="small" variant="standard" value={a.status || 'OPEN'}
                      onChange={(e) => runAction(setFindingStatus, { id: a.id, status: e.target.value })}
                      sx={{ fontSize: '0.68rem' }}
                    >
                      {FINDING_STATUSES.map((s) => <MenuItem key={s} value={s} sx={{ fontSize: '0.76rem' }}>{s.replaceAll('_', ' ')}</MenuItem>)}
                    </Select>
                  </Box>
                </Box>
              ))}
              {!alerts.length && <Typography sx={{ fontSize: '0.78rem', color: tokens.text.muted, textAlign: 'center', py: 2 }}>No endpoint alerts.</Typography>}
            </Stack>
          </Paper>

          <Paper sx={{ p: 2.4 }}>
            <Typography variant="overline" sx={{ color: tokens.text.muted, fontSize: '0.65rem' }}>Pending Response Commands</Typography>
            <Typography sx={{ fontSize: '0.7rem', color: tokens.text.muted, mb: 1.5 }}>The requester cannot approve their own command.</Typography>
            <Stack spacing={1} sx={{ mt: 1 }}>
              {commands.map((c) => (
                <Box key={c.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, borderRadius: 1.5, border: `1px solid ${tokens.border}` }}>
                  <Box>
                    <Typography sx={{ fontSize: '0.78rem', color: tokens.text.primary }}>{c.action.replaceAll('_', ' ')}</Typography>
                    <Typography sx={{ fontSize: '0.68rem', color: tokens.text.muted, maxWidth: 240 }} noWrap>{c.reason}</Typography>
                  </Box>
                  <Stack direction="row" spacing={0.5}>
                    <Tooltip title="Approve"><IconButton size="small" onClick={() => runAction(approvePendingCommand, c.id)}><CheckRoundedIcon fontSize="small" sx={{ color: tokens.severity.low }} /></IconButton></Tooltip>
                    <Tooltip title="Reject"><IconButton size="small" onClick={() => setRejectTarget(c)}><CloseRoundedIcon fontSize="small" sx={{ color: tokens.severity.critical }} /></IconButton></Tooltip>
                  </Stack>
                </Box>
              ))}
              {!commands.length && <Typography sx={{ fontSize: '0.78rem', color: tokens.text.muted, textAlign: 'center', py: 2 }}>No commands awaiting approval.</Typography>}
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      <Dialog open={registerOpen} onClose={() => setRegisterOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Register Device</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Hostname" size="small" autoFocus value={registerForm.hostname} onChange={(e) => setRegisterForm((f) => ({ ...f, hostname: e.target.value }))} />
            <TextField label="Operating System" size="small" value={registerForm.os} onChange={(e) => setRegisterForm((f) => ({ ...f, os: e.target.value }))} />
            <TextField label="IP Address" size="small" value={registerForm.ip_address} onChange={(e) => setRegisterForm((f) => ({ ...f, ip_address: e.target.value }))} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRegisterOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleRegister}>Register</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(rejectTarget)} onClose={() => setRejectTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Reject Command</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth multiline minRows={2} sx={{ mt: 1 }} label="Reason" value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)} helperText="Minimum 10 characters."
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRejectTarget(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleReject}>Reject</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
